import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, DollarSign } from "lucide-react";
import {
	WorkforceStatus,
	StatusChangeReason,
	STATUS_CHANGE_REASONS,
	BenchReason,
	BENCH_REASONS,
	CURRENCY_OPTIONS,
} from "@/types/workforce";

export interface BenchCreationData {
	bench_daily_cost: number;
	currency: string;
	bench_reason?: BenchReason;
	work_percentage?: number;
	notes?: string;
}

interface WorkforceStatusChangeModalProps {
	isOpen: boolean;
	onClose: () => void;
	currentStatus: WorkforceStatus;
	targetStatus: WorkforceStatus;
	candidateName?: string;
	candidateId?: string;
	workforceId?: string;
	currentAllocation?: number | null;
	currentCurrency?: string;
	isBench?: boolean;
	onConfirm: (data: {
		status: WorkforceStatus;
		end_date?: string;
		status_change_reason?: StatusChangeReason;
		status_change_note?: string;
		createBench?: BenchCreationData;
	}) => void;
}

export const WorkforceStatusChangeModal = ({
	isOpen,
	onClose,
	currentStatus,
	targetStatus,
	candidateName,
	currentAllocation,
	currentCurrency,
	isBench,
	onConfirm,
}: WorkforceStatusChangeModalProps) => {
	const { t } = useTranslation();

	const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
	const [reason, setReason] = useState<StatusChangeReason | "">("");
	const [note, setNote] = useState("");

	// Bench creation state
	const [createBench, setCreateBench] = useState(false);
	const [benchDailyCost, setBenchDailyCost] = useState("");
	const [benchCurrency, setBenchCurrency] = useState(currentCurrency || "EUR");
	const [benchReason, setBenchReason] = useState<BenchReason | "">("");
	const [benchAllocation, setBenchAllocation] = useState(currentAllocation?.toString() || "");
	const [benchNotes, setBenchNotes] = useState("");

	// Determine which fields are needed
	const needsEndDate = targetStatus === "completed" || targetStatus === "terminated";
	const needsReason = targetStatus === "completed" || targetStatus === "terminated";
	const isReactivation = (currentStatus === "completed" || currentStatus === "terminated") && targetStatus === "active";
	const canOfferBench = !isBench && (targetStatus === "completed" || targetStatus === "terminated" || targetStatus === "on_hold");

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setEndDate(new Date().toISOString().split("T")[0]);
			setReason("");
			setNote("");
			setCreateBench(false);
			setBenchDailyCost("");
			setBenchCurrency(currentCurrency || "EUR");
			setBenchReason("");
			setBenchAllocation(currentAllocation?.toString() || "");
			setBenchNotes("");
		}
	}, [isOpen, currentAllocation, currentCurrency]);

	const canSubmit = () => {
		if (needsEndDate && !endDate)
			return false;
		if (needsReason && !reason)
			return false;
		if (createBench && !benchDailyCost)
			return false;
		return true;
	};

	const handleConfirm = () => {
		const data: Parameters<typeof onConfirm>[0] = {
			status: targetStatus,
		};

		if (needsEndDate)
			data.end_date = endDate;

		if (needsReason && reason)
			data.status_change_reason = reason as StatusChangeReason;

		if (note.trim())
			data.status_change_note = note.trim();

		if (createBench && benchDailyCost) {
			data.createBench = {
				bench_daily_cost: parseFloat(benchDailyCost),
				currency: benchCurrency,
				bench_reason: benchReason ? benchReason as BenchReason : undefined,
				work_percentage: benchAllocation ? parseInt(benchAllocation) : undefined,
				notes: benchNotes.trim() || undefined,
			};
		}

		onConfirm(data);
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className={canOfferBench ? "sm:max-w-lg max-h-[85vh] overflow-y-auto" : "sm:max-w-md"}>
				<DialogHeader>
					<DialogTitle>
						{t("workforce.statusChange.title", "Change Assignment Status")}
					</DialogTitle>
					<DialogDescription>
						{t("workforce.statusChange.description", "Update the status of this assignment")}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					{/* Reactivation warning */}
					{isReactivation && (
						<div className="flex items-start gap-3 p-3 rounded-sm bg-amber-50 border border-amber-200 text-amber-800">
							<AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
							<div className="text-sm">
								<p className="font-medium">
									{t("workforce.statusChange.reactivationWarning", "Are you sure you want to reactivate this assignment?")}
								</p>
								<p className="mt-1 text-amber-700">
									{t("workforce.statusChange.reactivationHint", "Reactivating a completed or terminated assignment can make reporting more complex. Consider creating a new assignment instead.")}
								</p>
							</div>
						</div>
					)}

					{/* Status display */}
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<span>{t(`workforce.statuses.${currentStatus}`, currentStatus)}</span>
						<span>→</span>
						<span className="font-medium text-foreground">
							{t(`workforce.statuses.${targetStatus}`, targetStatus)}
						</span>
					</div>

					{/* Reason (mandatory when leaving active) */}
					{needsReason && (
						<div className="space-y-2">
							<Label className="text-sm font-medium">
								{t("workforce.statusChange.reason", "Reason")}
								<span className="text-red-500 ml-1">*</span>
							</Label>
							<Select value={reason} onValueChange={(v) => setReason(v as StatusChangeReason)}>
								<SelectTrigger className="h-11">
									<SelectValue placeholder={t("workforce.statusChange.selectReason", "Select a reason...")} />
								</SelectTrigger>
								<SelectContent>
									{STATUS_CHANGE_REASONS.map((r) => (
										<SelectItem key={r.value} value={r.value}>
											{t(`workforce.statusChange.reasons.${r.value}`, r.label)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{/* End date (mandatory when going to completed/terminated) */}
					{needsEndDate && (
						<div className="space-y-2">
							<Label className="text-sm font-medium">
								{t("workforce.fields.endDate", "End Date")}
								<span className="text-red-500 ml-1">*</span>
							</Label>
							<Input
								type="date"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								className="h-11"
							/>
						</div>
					)}

					{/* Optional note */}
					<div className="space-y-2">
						<Label className="text-sm font-medium">
							{t("workforce.statusChange.note", "Note")}
							<span className="text-muted-foreground text-xs ml-1">
								({t("common.optional", "optional")})
							</span>
						</Label>
						<Textarea
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder={t("workforce.statusChange.notePlaceholder", "Add a note about this status change...")}
							rows={2}
							className="resize-none"
						/>
					</div>

					{/* Create bench assignment option */}
					{canOfferBench && (
						<>
							<div className="border-t pt-4">
								<div className="flex items-center space-x-2">
									<Checkbox
										id="create-bench"
										checked={createBench}
										onCheckedChange={(checked) => setCreateBench(checked === true)}
									/>
									<Label htmlFor="create-bench" className="text-sm font-medium cursor-pointer">
										{t("workforce.statusChange.createBench", "Create bench assignment")}
									</Label>
								</div>
								{candidateName && (
									<p className="text-xs text-muted-foreground mt-1 ml-6">
										{t("workforce.statusChange.createBenchHint", "Add {{name}} to the bench to track availability and costs", { name: candidateName })}
									</p>
								)}
							</div>

							{createBench && (
								<div className="space-y-4 p-4 border rounded-sm bg-muted/30">
									{/* Daily Cost (mandatory) & Currency */}
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
										<div className="space-y-2">
											<Label className="text-sm font-medium">
												<DollarSign className="h-3.5 w-3.5 inline mr-1" />
												{t("workforce.bench.dailyCostLabel", "Daily Cost (what you pay them)")} *
											</Label>
											<Input
												type="number"
												min="0"
												step="0.01"
												value={benchDailyCost}
												onChange={(e) => setBenchDailyCost(e.target.value)}
												placeholder="0.00"
												className="h-10"
											/>
										</div>
										<div className="space-y-2">
											<Label className="text-sm font-medium">{t("workforce.fields.currency", "Currency")}</Label>
											<Select value={benchCurrency} onValueChange={setBenchCurrency}>
												<SelectTrigger className="h-10">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{CURRENCY_OPTIONS.map((c) => (
														<SelectItem key={c} value={c}>{c}</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</div>

									{/* Allocation % */}
									<div className="space-y-2">
										<Label className="text-sm font-medium">
											{t("workforce.fields.workPercentage", "Allocation %")}
										</Label>
										<Input
											type="number"
											min="0"
											max="100"
											value={benchAllocation}
											onChange={(e) => setBenchAllocation(e.target.value)}
											placeholder="100"
											className="h-10"
										/>
									</div>

									{/* Bench Reason */}
									<div className="space-y-2">
										<Label className="text-sm font-medium">{t("workforce.bench.reason", "Reason")}</Label>
										<Select value={benchReason} onValueChange={(v) => setBenchReason(v as BenchReason)}>
											<SelectTrigger className="h-10">
												<SelectValue placeholder={t("workforce.bench.selectReason", "Select reason...")} />
											</SelectTrigger>
											<SelectContent>
												{BENCH_REASONS.map((r) => (
													<SelectItem key={r.value} value={r.value}>
														{t(`workforce.bench.reasons.${r.value}`, r.label)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									{/* Notes */}
									<div className="space-y-2">
										<Label className="text-sm font-medium">
											{t("workforce.bench.notes", "Notes")}
										</Label>
										<Textarea
											value={benchNotes}
											onChange={(e) => setBenchNotes(e.target.value)}
											placeholder={t("workforce.bench.notesPlaceholder", "Additional notes about this bench entry...")}
											rows={2}
											className="resize-none"
										/>
									</div>
								</div>
							)}
						</>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={!canSubmit()}
						variant={isReactivation ? "destructive" : "default"}
					>
						{t("workforce.statusChange.confirm", "Confirm")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
